// src/ScanHistoryModal.js
// Reusable scan-history modal: click a name (in Cardholders or History) to see
// that card's scans, paginated 25/page with numbered pages. Filters by card
// number via /badge-management. Shared by BadgeManagement + TX3CardManager.
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import './styles/ScanHistoryModal.css';

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function getPageWindow(current, total) {
  if (total <= 1) return [1];
  const delta = 2;
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);
  const pages = [1];
  if (left > 2) pages.push('…');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('…');
  pages.push(total);
  return pages;
}

export default function ScanHistoryModal({ title, cardNumber, onClose }) {
  const [state, setState] = useState({ scans: [], page: 1, pages: 1, total: 0, loading: true, error: null });
  const [range, setRange] = useState({ start: '', end: '' });

  const fetchPage = useCallback(async (page, start = '', end = '') => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      let url = `/badge-management?cardNumber=${encodeURIComponent(cardNumber)}&page=${page}&limit=25`;
      if (start) url += `&startDate=${start}`;
      if (end) url += `&endDate=${end}`;
      const { data } = await axiosInstance.get(url);
      setState({
        scans: data.scans || [],
        page: data.pagination?.page || 1,
        pages: data.pagination?.pages || 1,
        total: data.pagination?.total || 0,
        loading: false, error: null,
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err.response?.data?.message || 'Failed to load history.' }));
    }
  }, [cardNumber]);

  useEffect(() => { fetchPage(1, range.start, range.end); }, [fetchPage, range.start, range.end]);

  // close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const goTo = (p) => {
    const target = Math.max(1, Math.min(state.pages, Number(p) || 1));
    if (target !== state.page) fetchPage(target, range.start, range.end);
  };

  return (
    <div className="shm-overlay" onClick={onClose}>
      <div className="shm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="shm-head">
          <div>
            <h3>{title || '(Unnamed)'}</h3>
            <span className="shm-sub">Card #{cardNumber} · {state.total.toLocaleString()} scans</span>
          </div>
          <button type="button" className="shm-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="shm-body">
          <div className="shm-filters">
            <label>From <input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} /></label>
            <label>To <input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} /></label>
            {(range.start || range.end) && (
              <button type="button" className="shm-clear" onClick={() => setRange({ start: '', end: '' })}>Clear</button>
            )}
          </div>
          {state.error ? (
            <div className="shm-inline error">{state.error}</div>
          ) : state.loading ? (
            <div className="shm-loading">Loading history…</div>
          ) : state.scans.length === 0 ? (
            <p className="shm-empty">No scans for this card yet.</p>
          ) : (
            <>
              <div className="shm-table-wrap">
                <table className="shm-table">
                  <thead>
                    <tr><th>Time</th><th>Direction</th><th>Door</th><th>Access</th></tr>
                  </thead>
                  <tbody>
                    {state.scans.map((s) => (
                      <tr key={s._id}>
                        <td>{formatTime(s.timestamp)}</td>
                        <td>{s.direction === 'in' ? 'IN' : s.direction === 'out' ? 'OUT' : '—'}</td>
                        <td>{s.door}</td>
                        <td>
                          <span className={`shm-status ${s.accessGranted ? 'on' : 'off'}`}>
                            {s.accessGranted ? 'GRANTED' : 'DENIED'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {state.pages > 1 && (
                <div className="shm-pagination">
                  <button disabled={state.page <= 1} onClick={() => goTo(state.page - 1)}>Previous</button>
                  <div className="shm-page-numbers">
                    {getPageWindow(state.page, state.pages).map((p, i) =>
                      p === '…' ? (
                        <span key={`gap-${i}`} className="shm-page-ellipsis">…</span>
                      ) : (
                        <button
                          key={p}
                          className={p === state.page ? 'shm-page-num active' : 'shm-page-num'}
                          onClick={() => goTo(p)}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                  <button disabled={state.page >= state.pages} onClick={() => goTo(state.page + 1)}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
